import { useState, FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Modal } from "../../components/Modal/Modal";
import { Input } from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { Alert } from "../../components/Alert/Alert";
import { useToast } from "../../components/Toast/toastContext";
import { ApiError, describeError } from "../../lib/errors";
import { patchProject, ProjectDetail, type PatchedProject } from "./api";
import { projectQueryKey } from "./useProject";

interface Props {
  open: boolean;
  onClose: () => void;
  /** The record being renamed — and the ETag the PATCH is sent with, which is
   *  a field of it. */
  project: ProjectDetail;
  /** Notify caller after a successful rename so it can refresh dependent data. */
  onSaved?: (project: PatchedProject) => void;
}

// Mockup L464 ‒ "Rename" is the primary affordance on the project hero. The
// project-name input shares the same validation rules as Create/Config (2–80
// chars; ASCII alphanumerics + space + hyphen). Save sends an If-Match'd PATCH
// and surfaces 409 etag_mismatch via toast so the user knows to reload.
const NAME_PATTERN = /^[A-Za-z0-9 -]+$/;

/**
 * The form is mounted by opening and thrown away by closing, so the input is
 * seeded with the stored name on every open. A rename that lands elsewhere
 * while this dialog is open no longer overwrites what the operator is typing —
 * and the one case that used to need it, a 409, closes the dialog anyway.
 */
export function RenameProjectModal({ open, ...rest }: Props) {
  if (!open) return null;
  return <RenameProjectForm {...rest} />;
}

function RenameProjectForm({
  onClose,
  project,
  onSaved,
}: Omit<Props, "open">) {
  const qc = useQueryClient();
  const { push } = useToast();
  const [name, setName] = useState(project.name);
  const [error, setError] = useState<string | null>(null);

  const trimmed = name.trim();
  const valid =
    trimmed.length >= 2 &&
    trimmed.length <= 80 &&
    NAME_PATTERN.test(trimmed);
  const dirty = trimmed !== project.name;

  const mut = useMutation({
    mutationFn: async () => {
      // Strip weak-validator + surrounding quotes before sending the raw token.
      const raw = (project.etag ?? "")
        .replace(/^W\//, "")
        .replace(/^"|"$/g, "");
      const r = await patchProject(project.id, { name: trimmed }, raw);
      return r;
    },
    onSuccess: async (r) => {
      push({ variant: "success", message: "Project renamed." });
      // Invalidate everything that displays the project name so dependent
      // surfaces (overview, list, breadcrumb) refresh on next render.
      await qc.invalidateQueries({ queryKey: projectQueryKey(project.id) });
      await qc.invalidateQueries({ queryKey: ["projects"] });
      await qc.invalidateQueries({
        queryKey: ["project", project.id, "activity"],
      });
      onSaved?.(r.data);
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "etag_mismatch") {
        push({
          variant: "warn",
          message:
            "Project was updated by someone else — reload to see the latest version.",
        });
        // Force a refetch and close — the caller will see fresh data.
        void qc.invalidateQueries({ queryKey: projectQueryKey(project.id) });
        onClose();
        return;
      }
      setError(
        err instanceof ApiError
          ? describeError(err.code)
          : describeError("internal"),
      );
    },
  });

  function submit() {
    setError(null);
    if (!valid) {
      setError(
        "Name must be 2–80 characters and contain only letters, numbers, spaces, or hyphens.",
      );
      return;
    }
    if (!dirty) {
      onClose();
      return;
    }
    mut.mutate();
  }

  function onFormSubmit(e: FormEvent) {
    e.preventDefault();
    submit();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Rename project"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={mut.isPending || !valid || !dirty}
          >
            {mut.isPending ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <form onSubmit={onFormSubmit}>
        {error && <Alert variant="danger">{error}</Alert>}
        <Input
          label="Project name"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          hint="2–80 characters. Letters, numbers, spaces, hyphens."
          required
        />
      </form>
    </Modal>
  );
}
